import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useGetProductWarehouses } from "../hooks/useGetProductWarehouses"
import { useUpdateProductWarehouses } from "../hooks/useUpdateProductWarehouses"
import type { ProductWarehouseFormValues } from "../schemas"
import { productWarehouseFormSchema } from "../schemas"
import { toExistintProductWarehouseInput, toProductWarehouseFormValues, toProductWarehouseInput } from "../utils"

import { ProductWarehouseForm } from "./ProductWarehouseForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { ProductWarehouseArea } from "@/graphql/codegen/graphql"

interface ProductWarehouseFormDialogProps {
  warehouseArea: ProductWarehouseArea | null
  productId: number
  version?: number
  onClose: () => void
  onSubmit: () => void
}

export const ProductWarehouseFormDialog: FC<ProductWarehouseFormDialogProps> = ({
  warehouseArea,
  productId,
  version,
  onClose,
  onSubmit,
}) => {
  const isEdit = !!warehouseArea
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const { updateProductWarehouses } = useUpdateProductWarehouses({ productId, version })
  // Always includes inactive — submit below fully replaces the product's warehouse collection, so a
  // toggle-filtered fetch would drop hidden rows.
  const { warehouseAreas: existingWarehouseAreas, isFetching: isFetchingWarehouseAreas } = useGetProductWarehouses({
    productId,
    version,
    includeInactive: true,
  })

  const methods = useForm<ProductWarehouseFormValues>({
    defaultValues: toProductWarehouseFormValues(warehouseArea),
    resolver: zodResolver(productWarehouseFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isValid, isDirty, isSubmitting },
  } = methods

  const canSubmit = isValid && isDirty && !isSubmitting && !isFetchingWarehouseAreas

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const handleSubmit = methods.handleSubmit(async (formValues) => {
    const input = toProductWarehouseInput(formValues)

    const warehouseAreas = isEdit
      ? existingWarehouseAreas.map((existingWarehouseArea) =>
          existingWarehouseArea.warehouseAreaId === warehouseArea.warehouseAreaId
            ? { ...input, warehouseAreaId: existingWarehouseArea.warehouseAreaId }
            : toExistintProductWarehouseInput(existingWarehouseArea)
        )
      : [...existingWarehouseAreas.map(toExistintProductWarehouseInput), input]

    const isSuccess = await updateProductWarehouses(warehouseAreas, isEdit)

    if (isSuccess) {
      onSubmit()
    }
  })

  return (
    <Dialog open onClose={handleCancel} maxWidth='sm' fullWidth data-testid='product.warehouse-form-dialog'>
      {isCancelConfirmationOpen ? (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogHeader title={isEdit ? "Edit Warehouse Details" : "Add Warehouse Details"} onClose={handleCancel} />
          <DialogContent className='p-10'>
            <FormProvider {...methods}>
              <ProductWarehouseForm isEdit={isEdit} existingWarehouseAreas={existingWarehouseAreas} />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button
              appearance='outlined'
              onClick={handleCancel}
              data-testid='product.warehouse-form-dialog.cancel-button'
            >
              Cancel
            </Button>
            <Button
              appearance='contained'
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid='product.warehouse-form-dialog.save-button'
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
